import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { ChevronRight, ClipboardCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"

export type Crumb = { label: string; href: string }
export type PageMeta = { total_count: number; page: number; per_page: number }

// tracked_category (data-rule derived in M1/M2) → a human label for the UI.
export const CATEGORY_LABELS: Record<string, string> = {
  female_fragrance: "Parfum Wanita",
  male_fragrance: "Parfum Pria",
  feminine_wash: "Feminine Wash",
  other: "Lainnya",
}

export function categoryLabel(value: string | null): string | null {
  if (!value) return null
  return CATEGORY_LABELS[value] ?? value
}

export function OwnBadge({ isOwn }: { isOwn: boolean }) {
  return isOwn ? (
    <Badge tone="accent">Sendiri</Badge>
  ) : (
    <Badge tone="muted">Kompetitor</Badge>
  )
}

// The breadcrumb across the drill-down (Produk Master ▸ Manufacturer ▸ Brand …).
export function Breadcrumb({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <nav className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-ink-muted" aria-label="Breadcrumb">
      <Link href="/admin/product-master" className="hover:text-ink-body">
        Produk Master
      </Link>
      {crumbs.map((c, i) => (
        <React.Fragment key={c.href}>
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
          {i === crumbs.length - 1 ? (
            <span className="font-medium text-ink-body">{c.label}</span>
          ) : (
            <Link href={c.href} className="hover:text-ink-body">
              {c.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  )
}

// A link to the HITL review queue, with the live unresolved-alias count.
export function ReviewButton() {
  const { props } = usePage<PageProps>()
  const dq = props.product_master_dq_count ?? 0
  return (
    <Button asChild variant="secondary">
      <Link href="/admin/product-aliases">
        <ClipboardCheck className="mr-2 h-4 w-4" aria-hidden="true" />
        Antrian Review{dq > 0 ? ` (${dq})` : ""}
      </Link>
    </Button>
  )
}

export function Pagination({
  meta,
  path,
  query,
}: {
  meta: PageMeta
  path: string
  query: Record<string, string | undefined | null>
}) {
  const totalPages = Math.max(1, Math.ceil(meta.total_count / meta.per_page))
  if (totalPages <= 1) return null

  const go = (page: number) => {
    const q: Record<string, string> = {}
    Object.entries(query).forEach(([k, v]) => {
      if (v != null && v !== "") q[k] = String(v)
    })
    q.page = String(page)
    router.get(path, q, { preserveState: true, preserveScroll: true, replace: true })
  }

  return (
    <div className="mt-6 flex items-center justify-between text-sm text-ink-muted">
      <span>
        Halaman {meta.page} dari {totalPages} ({meta.total_count})
      </span>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" disabled={meta.page <= 1} onClick={() => go(meta.page - 1)}>
          Sebelumnya
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={meta.page >= totalPages}
          onClick={() => go(meta.page + 1)}
        >
          Berikutnya
        </Button>
      </div>
    </div>
  )
}

// A debounced text filter that pushes ?search= (and resets to page 1) on submit.
export function SearchForm({
  path,
  query,
  initial,
  placeholder,
}: {
  path: string
  query: Record<string, string | undefined | null>
  initial: string
  placeholder: string
}) {
  const [value, setValue] = React.useState(initial)
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const q: Record<string, string> = {}
    Object.entries(query).forEach(([k, v]) => {
      if (v != null && v !== "") q[k] = String(v)
    })
    if (value.trim()) q.search = value.trim()
    else delete q.search
    router.get(path, q, { preserveState: true, preserveScroll: true, replace: true })
  }
  return (
    <form onSubmit={submit} className="flex-1 min-w-[220px]">
      <label htmlFor="pm-search">Cari</label>
      <Input
        id="pm-search"
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className="mt-1"
      />
    </form>
  )
}
